import { cn } from '../lib/utils';

type Level = 1 | 2 | 3 | 4 | 5 | 6;

/**
 * This is the PRODUCT-UI scale: h1 tops out at 20→24px, not 30→36px.
 *
 * Dashboard page titles in the apps this is modelled on sit around 20px — a
 * 36px h1 is a marketing-page size, and using one scale for both surfaces is
 * what made every admin screen read as oversized. Marketing pages that genuinely
 * want a display size opt in explicitly via `className` (`text-5xl` etc.) rather
 * than every dashboard inheriting a hero scale it never asked for.
 *
 * Letter-spacing is deliberately absent: every size token in the type scale
 * (apps/web/styles/theme.css) already carries its own optical tracking, which
 * tightens as size grows. A `tracking-tight` here would flatten that curve and
 * make a 24px title track identically to a 16px one.
 *
 * Weight never exceeds `font-semibold` (590 on Inter's variable axis) —
 * hierarchy comes from size and contrast, not from stacking weight.
 */
const levels: Record<Level, string> = {
  1: 'text-xl font-semibold lg:text-2xl',
  // pb-2 / first:mt-0 / transition-colors are load-bearing layout carried over
  // from the original component — this pass changes type only.
  2: 'text-lg font-semibold pb-2 transition-colors first:mt-0 lg:text-xl',
  3: 'text-base font-semibold lg:text-lg',
  4: 'text-base font-medium',
  5: 'text-sm font-medium',
  6: 'text-sm font-medium',
};

export function Heading({
  level = 1,
  children,
  className,
}: React.PropsWithChildren<{ level?: Level; className?: string }>) {
  const Comp = `h${level}` as const;

  return (
    <Comp className={cn('font-heading scroll-m-20', levels[level], className)}>
      {children}
    </Comp>
  );
}
